import React, { useState, useEffect, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { getCSSPropertyValue } from "../../../utils/styles";

enum ChartCategory {
  MONTH = "month",
  CITY = "city",
  VENDOR = "vendor",
}

export default function VendorAuditBarChart({ data, category = ChartCategory.MONTH, loading = false }: any) {
  const [options, setOptions] = useState<any>(null);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const loaderTimeoutRef = useRef<any>(null);
  const singleBarLabelMapRef = useRef<Map<string, boolean>>(new Map());

  const [defaultOption] = useState<any>({
    color: ["#58AAFC","#477DBA"], 
    // color: ["#477DBA","#58AAFC"], 
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "none",
        label: {
          show:false,
        },
      },
    },
    legend: {
      data: ["Overall Score","Critical Score"],
      bottom: 0,
      fontSize:14,
      icon:'circle',
      selectedMode: false,
    },
    grid: {
      left: "1%",
      right: "1%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: [
    {
        type: "category",
        gridIndex: 0,
        boundaryGap: true,
        axisTick: {
          alignWithLabel: false,
          show: true,
          lineStyle: {
            color: '#E5E7EB'
          }
        },
        axisLabel: {
          interval: 0, 
          formatter: function(value: string) {
            const isSingleForThisLabel =
              singleBarLabelMapRef.current.get(value);
            let formattedValue = value;
            if (value.length > 20) {
              const maxLineLength = 30;
              const maxLines = 3;
              const lines = [];
              const words = value.split(' ');
              let currentLine = '';

              for (let i = 0; i < words.length; i++) {
                const word = words[i];
                const testLine = currentLine + (currentLine ? ' ' : '') + word;

                if (testLine.length <= maxLineLength) {
                  currentLine = testLine;
                } else {
                  if (currentLine) {
                    lines.push(currentLine);
                    if (lines.length >= maxLines) {
                      formattedValue = lines.join("\n") + "...";
                      break;
                    }
                    currentLine = word;
                  } else {
                    formattedValue = word.substring(0, maxLineLength - 3) + "...";
                    break;
                  }
                }
              }

              if (currentLine && lines.length < maxLines) {
                lines.push(currentLine);
              }

              if (!formattedValue.includes("\n")) {
                formattedValue = lines.join("\n");
              }
            }
            if (isSingleForThisLabel) {
              return `{leftShift|${formattedValue}}`;
            }

            return formattedValue;
          },
          rich: {
            leftShift: {
              padding: [0, 0, 0, -30],
              align: "left",
              color: "#000000",
              fontSize: 12,
              lineHeight: 16,
            },
          },
          fontSize: 12,
          margin: 20,
          lineHeight: 16,
          color: "#000000",
        },
        splitLine: {
          show: true,
          interval: 0,             
          lineStyle: {
            type: 'dashed',
            color: '#E5E7EB',
            width: 1
          }
        },
        axisLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#E5E7EB',
            width: 1
          }
        }
      },
    ],
    yAxis: [
      {
        type: "value",
        min: 0,
        max: 100,
        axisLabel: {
          formatter: "{value}",
          color: '#000000',
          fontSize: 12,
        },
         axisTick: {
          alignWithLabel: true,
        },
        splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#E5E7EB',
              width: 1
            }
        },
        axisLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#E5E7EB',
            width: 1
          }
        }
      },
      {
        // Second yAxis for right-side border as ECharts doesn't support borderType for grid borders
        type: "value",
        position: "right",
        min: 0,
        max: 100,
        axisLabel: {
          show: false,  
        },
        axisTick: {
          show: false,  
        },
        splitLine: {
          show: false,  
        },
        axisLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#E5E7EB',
            width: 1
          }
        }
      }
    ],
    dataset: {
      source: [],
    },
    series: [
      {
        name: "Overall Score",
        type: "bar",
        barWidth: 32,
        label: {
          show: false,
          position: "top",
          formatter: "{@[1]}",
        },
      },
      {
        name: "Critical Score",
        type: "bar",
        barWidth: 32,
        label: {
          show: false,
          position: "top",
          formatter: "{@[2]}",
        },
      },
    ],
  });

  useEffect(() => {
    // Handle case when data is undefined/null - show empty chart
    if (!data) {
      const _options = {
        ...defaultOption,
        xAxis: defaultOption.xAxis.map((axis: any) => ({
          ...axis,
          axisLabel: { ...axis.axisLabel }
        })),
        dataset: {
          source: [["category", "Overall Score", "Critical Score"]]
        }
      };
      setOptions(_options);
      return;
    }
    // if (data && category) {
    //   const source: any[] = [];
    //   let response;

    //   // Check if data has the category key (old mock data structure)
    //   // or if data.data exists (API response structure)
    //   if (data[category]) {
    //     response = data[category];
    //   } else if (data.data) {
    //     response = data.data;
    //   }
    if (data && category) {
       const source: any[] = [];
       let response;

       // Check if data has the category key (old mock data structure)
       // or if data.data exists (API response structure)
       if (data[category]) {
         response = data[category];
       } else if (data.data) {
         response = data.data;
       }

       // If no response or empty response, show "No Data Available"
       if (!response || response.length === 0) {
         const _options = {
           ...defaultOption,
           xAxis: defaultOption.xAxis.map((axis: any) => ({
             ...axis,
             axisLabel: { ...axis.axisLabel }
           })),
           dataset: {
             source: [["category", "Overall Score", "Critical Score"]]
           }
         };
         setOptions(_options);
         return;
       }

      if (response && response.length > 0) {
          // First row is headers
          source.push(["category", "Overall Score", "Critical Score"]);

          // Helper function to parse percentage string to number
          const parsePercentage = (percentageStr: string) => {
            if (!percentageStr) return 0;
            return parseFloat(percentageStr.replace('%', '')) || 0;
          };

          // Track if we have any meaningful data (non-zero values)
          // let hasData = false;

          // // Data rows - parse the overall_score and overall_critical_score objects
          // response.forEach((item: any) => {
          //   const name = item.name || item.label || item.vendor_name || item.location_name || item.month || "N/A";

          //   // Extract compliant percentage from overall_score and overall_critical_score
          //   const overallScore = item.overall_score && item.overall_score.compliant
          //     ? parsePercentage(item.overall_score.compliant)
          //     : 0;

          //   const criticalScore = item.overall_critical_score && item.overall_critical_score.compliant
          //     ? parsePercentage(item.overall_critical_score.compliant)
          //     : 0;

          //   // Check if any score is non-zero.
          //   if (overallScore > 0 || criticalScore > 0) {
          //     hasData = true;
          //   }

          //   source.push([name, overallScore, criticalScore]);
          // });
          let hasData = false;
            const nameCount = new Map<string, number>();

            // Data rows - parse the overall_score and overall_critical_score objects
            response.forEach((item: any) => {
              let name = item.name || item.label || item.vendor_name || item.city_name || item.month || "N/A";

              // Handle duplicate names by appending a counter
              if (nameCount.has(name)) {
                const count = nameCount.get(name)! + 1;
                nameCount.set(name, count);
                name = `${name} (${count})`;
              } else {
                nameCount.set(name, 1);
              }

              // Extract compliant percentage from overall_score and overall_critical_score
              const overallScore = item.overall_score && item.overall_score.compliant
                ? parsePercentage(item.overall_score.compliant)
                : 0;

              const criticalScore = item.overall_critical_score && item.overall_critical_score.compliant
                ? parsePercentage(item.overall_critical_score.compliant)
                : 0;

              // Check if any score is non-zero
              // if (overallScore > 0 || criticalScore > 0) {
              //   hasData = true;
              // }
              hasData = true;

              source.push([name, overallScore, criticalScore]);
            });

            const singleBarMap = new Map<string, boolean>();

            source.slice(1).forEach((row: any[]) => {
              const label = row[0];
              const overall = Number(row[1]) || 0;
              const critical = Number(row[2]) || 0;
              const visibleBars = [overall, critical].filter(v => v > 0).length;
              singleBarMap.set(label, visibleBars === 1);
            });
            singleBarLabelMapRef.current = singleBarMap;
          // If all values are 0, show "No Data Available"
          if (!hasData) {
            const _options = {
              ...defaultOption,
              xAxis: defaultOption.xAxis.map((axis: any) => ({
                ...axis,
                axisLabel: { ...axis.axisLabel }
              })),
              dataset: {
                source: [["category", "Critical Score", "Overall Score"]]
              }
            };
            setOptions(_options);
          } else {
            // Show chart with data
            const _options = {
              ...defaultOption,
              xAxis: defaultOption.xAxis.map((axis: any) => ({
                ...axis,
                axisLabel: { ...axis.axisLabel }
              })),
              dataset: {
                source: source
              }
            };
            setOptions(_options);
          }
        }
    }
  }, [category, data]);

  // Loader only shows on initial page load, not on filter/category changes
  useEffect(() => {
    if (loading && isInitialLoad) {
      setShowLoader(true);
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    } else if (!loading && isInitialLoad) {
      loaderTimeoutRef.current = setTimeout(() => {
        setShowLoader(false);
        setIsInitialLoad(false); // Mark that initial load is complete
      }, 1000);
    }
    return () => {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    };
  }, [loading, isInitialLoad]);

    const getTitleByCategory = () => {
        switch (category) {
            case ChartCategory.MONTH:
            return (
                <>
                <span className="text-appprimary">Month-wise</span>
                <span style={{ color: '#0F0F0F' }}> Compliance Score Trends</span>
                </>
            );
            case ChartCategory.CITY:
            return (
                <>
                <span className="text-appprimary">City-wise</span>
                <span style={{ color: '#0F0F0F' }}> Compliance Score Trends</span>
                </>
            );
            case ChartCategory.VENDOR:
            return (
                <>
                <span className="text-appprimary">Vendor-wise</span>
                <span style={{ color: '#0F0F0F' }}> Compliance Score Trends</span>
                </>
            );
            default:
            return <span style={{ color: '#0F0F0F' }}>Compliance Score Trends</span>;
        }
    };


  return (
    <>
      <div className="mb-3">
        <div className="text-appprimary text-title fw-semibold">
          {getTitleByCategory()}
        </div>
      </div>
      <div className="w-100" style={{ position: "relative" }}>
          {showLoader ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <ReactECharts 
                option={options || defaultOption} 
                style={{ height: "500px" }} 
              />
              {options && options.dataset && options.dataset.source && options.dataset.source.length <= 1 && (
                <div 
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "24px",
                    color: "#D9D9D9",
                    fontWeight: 600,
                    pointerEvents: "none"
                  }}
                >
                  No Data Available
                </div>
              )}
            </>
          )}
        </div>
    </>
  );
}
